//  Angular imports
import { Component, DestroyRef, ElementRef, inject, linkedSignal, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
//  Application imports
import { environment } from 'environments/environment';
import { ApplicationRoute } from 'app/models/application-route';
import { HKeyManagerService } from 'app/services/hkey-manager.service';
import { RAccount } from 'app/records/account.record';
import { RAccountDetails } from 'app/records/account-details.record';
import { RHKeyData } from 'app/records/h-key-data.record';
import { RMenuItem, RSubMenuItem } from 'app/records/menu-item.record';
import { StorageItem } from 'app/models/storage-item';
import { UserService } from 'app/services/user.service';
import { WINDOW_TOKEN } from 'app/window-token';
//  Third party imports
import { fromEvent } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { NgIf, NgFor, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OrgChooserComponent } from '../organization-chooser/org-chooser.component';

@Component({
    selector: 'zf-banner',
    templateUrl: './banner.component.html',
    styleUrls: ['./banner.component.scss'],
    imports: [NgIf, FormsModule, NgFor, OrgChooserComponent,
        RouterModule, CommonModule]
})
export class BannerComponent {
    userService = inject(UserService);
    sanitizer = inject(DomSanitizer);
    private cookieService = inject(CookieService);
    private hKeyManagerService = inject(HKeyManagerService);
    private window = inject<Window>(WINDOW_TOKEN);
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);

    public get registerUrl(): string {
        return environment.registerUrl;
    }

    public get fortifiedHKeyData(): string {
        return JSON.stringify(RHKeyData.toFortinet(this.hKeyData));
    }

    public readonly isServiceMenuCollapsed = signal<boolean>(true);

    public readonly isAuthenticated = this.userService.isAuthenticated;
    public readonly userId = this.userService.userId;
    public readonly orgNodes = this.userService.organizations;
    public readonly selectedAccount = this.userService.accountSelected;

    public readonly selectedPortal = signal<RSubMenuItem | RMenuItem>(null);

    // The menu that is currently dropped down. Closes whenever the selected account
    // changes, and is otherwise whatever toggleDropdown() last set.
    public readonly open = linkedSignal<RAccount, HTMLDivElement>({
        source: this.selectedAccount,
        computation: (): HTMLDivElement => null
    });

    private get hKeyData(): RHKeyData {
        return this.hKeyManagerService.get();
    }

    private readonly services = viewChild<ElementRef<HTMLDivElement>>('services');
    private readonly support = viewChild<ElementRef<HTMLDivElement>>('support');
    private readonly user = viewChild<ElementRef<HTMLDivElement>>('user');
    private readonly org = viewChild<ElementRef<HTMLDivElement>>('org');

    private get account(): RAccountDetails {
        return this.selectedAccount()?.details;
    }

    // Predetermined rules for when the service menu is collapsed
    // this is based on styling guide from FortiCloud team
    private serviceMenuCollapseLookup: Map<number, number> = new Map([
        [0, 4],
        [1, 5],
        [2, 9]
    ]);

    public toggle(event: MouseEvent) {
        event.stopPropagation();
        this.isServiceMenuCollapsed.update((collapsed: boolean): boolean => !collapsed);
    }

    public serviceMenuDisplay(menuHeaderItems: Array<RSubMenuItem>, index: number) {
        if (!this.isServiceMenuCollapsed()) {
            return menuHeaderItems;
        }

        const max = this.serviceMenuCollapseLookup.get(index);
        return menuHeaderItems.slice(0, max);
    }

    public selectPortal(portalMenuItem: RSubMenuItem, event: MouseEvent): void {
        if (portalMenuItem.visibility === 'ShowDisabled') {
            event.stopPropagation();
            event.preventDefault();
            return;
        }

        this.selectedPortal.set(portalMenuItem);
    }

    public onSubmit(e: Event): void {
        const portal: RSubMenuItem | RMenuItem = this.selectedPortal();
        if (portal == null || portal.displayName === environment.productName) {
            return;
        }
        let action: string = '';
        const isNewTab: boolean = true;
        if (portal.displayName.toLowerCase() === 'logout') {
            this.cookieService.delete(StorageItem.AUTH_TOKEN);
            this.hKeyManagerService.delete();
            this.router.navigateByUrl(ApplicationRoute.LOGOUT);
        } else if (portal.displayName.toLowerCase() === 'fortinethome') {
            // very specific use case for FortinetHome
            // they currently do not allow POSTing data to their site
            // so to ensure that we do not look foolish
            // we stop here, and update the location href
            window.location.href = portal.url;
            return;
        } else if (portal.displayName.toLowerCase() === 'switch accounts') {
            // we will re-route to account selection
            this.hKeyManagerService.delete();
            this.userService.selectAccount(null);
            this.router.navigateByUrl(ApplicationRoute.ACCOUNT_SELECTION);
        } else if (!!this.hKeyData && this.hKeyData.hasLastVisited(portal.displayName)) {
            action = this.hKeyData.getLastVisited(portal.displayName);
        } else if (
            !this.account ||
            this.account.allAssetsAccess === null ||
            this.account.allAssetsAccess
        ) {
            action = portal.url;
        } else if (portal.visibility === undefined || portal.visibility === 'ShowEnabled') {
            // sub-user
            // they do not have all access enabled
            // each portal is enabled/disabled by an admin
            // this also covers IAM user too
            action = portal.url;
        }

        if (action !== '') {
            (e.target as HTMLFormElement).action = action;
            (e.target as HTMLFormElement).target = isNewTab ? '_blank' : '';
            (e.target as HTMLFormElement).submit();
        }
    }

    public defaultLogout() {
        const logOut = new RSubMenuItem();
        logOut.displayName = 'Logout';
        logOut.url = environment.logoutUrl;
        this.selectedPortal.set(logOut);
    }

    public forceOrgDropdown() {
        this.toggleDropdown(this.org());
    }

    public forceUserDropdown() {
        this.toggleDropdown(this.user());
    }

    // The event has to be stopped here, the same way toggle() does it. toggleDropdown
    // subscribes to document clicks to close the menu again, and without this the
    // very click that opened the menu carries on bubbling into that brand new
    // listener within the same dispatch. open then changes underneath a change
    // detection pass that has already read it, which is an NG0100 on the
    // [hidden]="open !== support" binding.
    public forceServices(event: MouseEvent) {
        event.stopPropagation();
        this.toggleDropdown(this.services());
    }

    public forceSupport(event: MouseEvent) {
        event.stopPropagation();
        this.toggleDropdown(this.support());
    }

    private toggleDropdown(menu: ElementRef<HTMLDivElement>): void {
        if (this.open() === menu.nativeElement) {
            this.open.set(null);
        } else {
            this.open.set(menu.nativeElement);
            fromEvent(document, 'click')
                .pipe(
                    takeWhile((): boolean => this.open() === menu.nativeElement),
                    takeUntilDestroyed(this.destroyRef)
                ).subscribe(
                    (event: Event): void => {
                        if (!menu.nativeElement.contains(event.target as HTMLElement)) {
                            this.open.set(null);
                        }
                    }
                );
        }
    }
}
